import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

// The club's documents, read and signed before a coach can do anything else.
//
// A coach lands here on their first sign-in and cannot get past it. That is
// the point: these are safeguarding and conduct documents for people working
// with children, and "I'll read it later" is exactly what the club could not
// evidence before.
//
// The order is set by the server and the agreement is always last, because its
// declaration says the signer is familiar with the policies above it. The
// Sign button stays disabled until they are all ticked, and the server refuses
// it as well, so the rule holds whichever way the request arrives.

const Agreements = () => {
  const { user, refreshAgreements } = useAuth();
  const [docs, setDocs] = useState([]);
  const [outstanding, setOutstanding] = useState(null);
  const [opened, setOpened] = useState({});   // which ones they have actually clicked into
  const [signedName, setSignedName] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await api.get('/api/agreements');
      setDocs(res.data.documents);
      setOutstanding(res.data.outstanding);
    } catch (err) {
      setError(err.response?.data?.error || 'The club documents could not be loaded.');
    }
  };
  useEffect(() => { load(); }, []);

  // Opening the file is what turns the tick box on.
  //
  // Nobody can be made to READ anything, and pretending otherwise would be
  // theatre. But a tick box that can be ticked without the document ever being
  // opened is worth nothing at all, so the one thing that is enforced is that
  // the document was successfully fetched. It is an honest middle: the club can
  // say the document was put in front of them and they confirmed it.
  //
  // WHY THIS FETCHES THE FILE RATHER THAN JUST LINKING TO IT. The route needs
  // an Authorization header, and the token lives in localStorage where only
  // our own requests can reach it. A plain link or window.open is an ordinary
  // browser navigation that sends no header, so it came back 401 and the
  // coach could not open a single document. Caught in a browser, not by the
  // tests, which were sending the token themselves like a well-behaved robot.
  //
  // The blank tab is opened FIRST, inside the click, and pointed at the file
  // once it has downloaded. Open it afterwards instead and the browser treats
  // it as a pop-up that no one asked for and blocks it.
  const openDoc = async (doc) => {
    setError('');
    const tab = window.open('', '_blank');
    try {
      const res = await api.get(`/api/agreements/${doc.key}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (tab) {
        tab.location = url;
      } else {
        // Pop-ups blocked. Fall back to downloading it, which never is.
        const a = document.createElement('a');
        a.href = url;
        a.download = `${doc.title}${doc.opensInBrowser ? '.pdf' : '.docx'}`;
        a.click();
      }
      // Long enough for the tab to have loaded it, then released. Holding
      // every policy a coach opens in memory for the whole session is how a
      // phone with ten tabs open starts to struggle.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      // Only now does the tick box unlock: if it failed, they have not read it.
      setOpened(prev => ({ ...prev, [doc.key]: true }));
    } catch (err) {
      if (tab) tab.close();
      setError(`${doc.title} could not be opened. Try again, and tell the club if it keeps happening.`);
    }
  };

  const agree = async (doc, name) => {
    setBusy(doc.key); setError('');
    try {
      await api.post(`/api/agreements/${doc.key}`, {
        version: doc.version,
        ...(name ? { signed_name: name } : {})
      });
      await load();
      // Tells the top-level gate the count has moved, so finishing the last one
      // lets them straight into the portal rather than leaving them stuck here.
      await refreshAgreements();
    } catch (err) {
      setError(err.response?.data?.error || 'That could not be recorded.');
    } finally {
      setBusy('');
    }
  };

  const policies = docs.filter(d => d.action !== 'sign');
  const agreement = docs.find(d => d.action === 'sign');
  const policiesDone = policies.every(d => d.done);
  const allDone = outstanding === 0;

  return (
    <div className="container">
      <h1>Before you start</h1>
      <p className="muted agreements-intro">
        {allDone
          ? 'You have read and signed everything the club needs. These are here whenever you want to look at them again.'
          : 'The club needs you to read these and confirm it. You only have to do this once, '
            + 'and the portal will ask again only if the club changes one of them.'}
      </p>

      {error && <div className="error-msg prewrap">{error}</div>}

      <div className="card">
        <h2>Read these</h2>
        {policies.map(doc => (
          <div key={doc.key} className={`doc-row${doc.done ? ' done' : ''}`}>
            <div className="doc-main">
              <div className="doc-title">
                {doc.title}
                {doc.done && <span className="badge paid doc-badge">Read</span>}
                {doc.changed && <span className="badge submitted doc-badge">Updated, please read again</span>}
              </div>
              <p className="muted doc-summary">{doc.summary}</p>
              {doc.done && doc.agreed_at && (
                <p className="muted doc-summary">
                  Confirmed {new Date(doc.agreed_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              )}
            </div>
            <div className="doc-actions">
              <button className="btn secondary small" onClick={() => openDoc(doc)}>
                {doc.opensInBrowser ? 'Open' : 'Download'}
              </button>
              {!doc.done && (
                <button
                  className="btn small"
                  disabled={!opened[doc.key] || busy === doc.key}
                  onClick={() => agree(doc)}
                  title={!opened[doc.key] ? 'Open the document first' : undefined}
                >
                  {busy === doc.key ? 'Saving…' : 'I have read this'}
                </button>
              )}
            </div>
          </div>
        ))}
        {policies.length === 0 && <p className="muted">No documents have been set up yet.</p>}
      </div>

      {agreement && (
        <div className="card">
          <h2>{agreement.title}</h2>
          <p className="muted doc-summary">{agreement.summary}</p>

          {agreement.done ? (
            <>
              <div className="success-msg">
                Signed by {agreement.signed_name} on{' '}
                {new Date(agreement.agreed_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}.
              </div>
              <button className="btn secondary small" onClick={() => openDoc(agreement)}>Open the agreement</button>
            </>
          ) : (
            <>
              <button className="btn secondary small" onClick={() => openDoc(agreement)}>Read the agreement</button>

              {agreement.declaration && (
                <blockquote className="declaration">{agreement.declaration}</blockquote>
              )}

              {!policiesDone && (
                <div className="notice-msg">
                  Read and confirm the documents above first. The declaration says you are familiar
                  with them, so it would not be true yet.
                </div>
              )}

              <form
                className="sign-form"
                onSubmit={e => { e.preventDefault(); agree(agreement, signedName); }}
              >
                <div className="form-row">
                  <label htmlFor="signed-name">Type your full name to sign</label>
                  <input
                    id="signed-name"
                    name="signed_name"
                    autoComplete="name"
                    value={signedName}
                    onChange={e => setSignedName(e.target.value)}
                    placeholder={user?.name || ''}
                    disabled={!policiesDone}
                    required
                  />
                  <p className="muted field-hint">
                    Typing your name here counts as your signature. The date, and the fact it was you
                    signed in, are recorded with it.
                  </p>
                </div>
                <button
                  className="btn"
                  type="submit"
                  disabled={!policiesDone || !signedName.trim() || !opened[agreement.key] || busy === agreement.key}
                  title={!opened[agreement.key] ? 'Read the agreement first' : undefined}
                >
                  {busy === agreement.key ? 'Signing…' : 'Sign the agreement'}
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Agreements;
